import { useCallback } from 'react';
import { toast } from 'sonner';

import { useBulkCapabilityJob } from '@/hooks/useCapability';
import {
  isSeedDomain,
  isSeedIP,
  isSeedWebApplication,
  Seed,
} from '@/sections/seeds/types';
import { useGlobalState } from '@/state/global.state';
import { pluralize } from '@/utils/pluralize.util';

/**
 * Return type for useSeedScanHandlers hook
 */
export interface SeedScanHandlers {
  /** Run web crawl on web application seeds */
  handleWebCrawl: (seeds: Seed[]) => void;
  /** Run web scan (DAST) on web application seeds */
  handleWebScan: (seeds: Seed[]) => Promise<void>;
  /** Run comprehensive scan on all seed types */
  handleComprehensiveScan: (seeds: Seed[]) => Promise<void>;
  /** Run network vulnerability scan on domain/IP seeds */
  handleNetworkScan: (seeds: Seed[]) => Promise<void>;
  /** Run port scan on domain/IP seeds */
  handlePortScan: (seeds: Seed[]) => Promise<void>;
}

/**
 * Hook for managing seed scan operations
 *
 * Provides handlers for initiating security scans on seeds with proper filtering,
 * error handling, and toast notifications.
 *
 * Features:
 * - Web crawl initiation (opens user agent modal)
 * - Web scan (DAST) execution via Burp
 * - Comprehensive scan (multiple capabilities based on seed type)
 * - Network scan (Nuclei vulnerability scanning)
 * - Port scan (TCP/UDP port enumeration)
 * - Automatic filtering for applicable seed types
 * - Toast notifications for scan status
 * - Bulk scan support
 *
 * @example
 * ```tsx
 * const {
 *   handleWebCrawl,
 *   handleWebScan,
 *   handleComprehensiveScan,
 *   handleNetworkScan,
 *   handlePortScan,
 * } = useSeedScanHandlers();
 *
 * // Start web crawl for selected seeds
 * handleWebCrawl(selectedSeeds);
 *
 * // Run DAST scan
 * await handleWebScan(selectedSeeds);
 *
 * // Run comprehensive scan
 * await handleComprehensiveScan(selectedSeeds);
 * ```
 */
export const useSeedScanHandlers = (): SeedScanHandlers => {
  const { mutateAsync: runJobs } = useBulkCapabilityJob();
  const {
    modal: { selectUserAgent },
  } = useGlobalState();

  /**
   * Open web crawl modal for web application seeds
   */
  const handleWebCrawl = useCallback(
    (seeds: Seed[]) => {
      const webAppSeeds = seeds.filter(seed => seed.class === 'webapplication');

      if (webAppSeeds.length === 0) {
        toast.error('No web applications found to crawl');
        return;
      }

      const webAppKeys = webAppSeeds.map(seed => seed.key);
      selectUserAgent.onAssetKeysChange(webAppKeys);

      toast.info(
        `Preparing to crawl ${webAppSeeds.length} ${pluralize(webAppSeeds.length, 'web application')}`
      );
    },
    [selectUserAgent]
  );

  /**
   * Run web scan (DAST) on web application seeds
   */
  const handleWebScan = useCallback(
    async (seeds: Seed[]) => {
      const webAppSeeds = seeds.filter(seed => seed.class === 'webapplication');

      if (webAppSeeds.length === 0) {
        toast.error('No web applications found to scan');
        return;
      }

      const jobsToRun = webAppSeeds.map(seed => ({
        assetKey: seed.key,
        capabilities: ['burp-dast-scan'],
        config: {},
      }));

      try {
        await runJobs(jobsToRun);
        toast.success(
          `Started web scan for ${webAppSeeds.length} ${pluralize(webAppSeeds.length, 'web application')}`
        );
      } catch {
        // useBulkCapabilityJob already surfaces an error toast on the all-failed path (its only rejection path); avoid double-toasting.
      }
    },
    [runJobs]
  );

  /**
   * Run comprehensive scan on all seed types
   * Different capabilities are run based on seed class:
   * - Domain/TLD: DNS enumeration, subdomain discovery, cert transparency
   * - IP/CIDR: Port scan, service detection, network scanning
   * - Web applications: Web crawl + Web scan + SSL analysis
   */
  const handleComprehensiveScan = useCallback(
    async (seeds: Seed[]) => {
      const jobsToRun: Array<{
        assetKey: string;
        config: Record<string, string>;
      }> = [];

      seeds.forEach(seed => {
        if (isSeedWebApplication(seed)) {
          jobsToRun.push(
            { assetKey: seed.key, config: {} },
            {
              assetKey: seed.key,
              config: {},
            }
          );
        } else if (isSeedDomain(seed)) {
          jobsToRun.push({
            assetKey: seed.key,
            config: {},
          });
        } else if (isSeedIP(seed)) {
          jobsToRun.push({
            assetKey: seed.key,
            config: {},
          });
        }
      });

      if (jobsToRun.length === 0) {
        toast.error('No applicable seeds found for comprehensive scan');
        return;
      }

      try {
        await runJobs(jobsToRun);
        toast.success(
          `Started comprehensive scan for ${seeds.length} ${pluralize(seeds.length, 'seed')}`
        );
      } catch {
        // useBulkCapabilityJob already surfaces an error toast on the all-failed path (its only rejection path); avoid double-toasting.
      }
    },
    [runJobs]
  );

  /**
   * Run network vulnerability scan (Nuclei) on domain/IP seeds
   * Web applications are filtered out as they use web-specific scans
   */
  const handleNetworkScan = useCallback(
    async (seeds: Seed[]) => {
      const applicableSeeds = seeds.filter(
        seed => isSeedDomain(seed) || isSeedIP(seed)
      );

      if (applicableSeeds.length === 0) {
        toast.error(
          'Network scan only applies to domain and IP seeds. Please select domain or IP seeds.'
        );
        return;
      }

      const jobsToRun = applicableSeeds.map(seed => ({
        assetKey: seed.key,
        capabilities: ['nuclei'],
        config: {},
      }));

      try {
        await runJobs(jobsToRun);
        toast.success(
          `Started network scan for ${applicableSeeds.length} ${pluralize(applicableSeeds.length, 'seed')}`
        );
      } catch {
        // useBulkCapabilityJob already surfaces an error toast on the all-failed path (its only rejection path); avoid double-toasting.
      }
    },
    [runJobs]
  );

  /**
   * Run port scan on domain/IP seeds
   * Web applications are filtered out as they use web-specific scans
   */
  const handlePortScan = useCallback(
    async (seeds: Seed[]) => {
      const applicableSeeds = seeds.filter(
        seed => isSeedDomain(seed) || isSeedIP(seed)
      );

      if (applicableSeeds.length === 0) {
        toast.error(
          'Port scan only applies to domain and IP seeds. Please select domain or IP seeds.'
        );
        return;
      }

      const jobsToRun = applicableSeeds.map(seed => ({
        assetKey: seed.key,
        capabilities: ['port-scan'],
        config: {},
      }));

      try {
        await runJobs(jobsToRun);
        toast.success(
          `Started port scan for ${applicableSeeds.length} ${pluralize(applicableSeeds.length, 'seed')}`
        );
      } catch {
        // useBulkCapabilityJob already surfaces an error toast on the all-failed path (its only rejection path); avoid double-toasting.
      }
    },
    [runJobs]
  );

  return {
    handleWebCrawl,
    handleWebScan,
    handleComprehensiveScan,
    handleNetworkScan,
    handlePortScan,
  };
};
